/**
 * The offerings piece (web-studio-sites D15): the soonest of everything the
 * studio offers, services and events in one list, each with its date (an
 * event) or its next opening (a service), its length, spots left when few,
 * and its price; each opens its page. The order is the server's
 * (`/api/site/offerings/soonest`). Draws nothing when nothing is open.
 */
import type { SiteOfferingDto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'offerings',
  description:
    'The soonest of everything the studio offers, services and events in one list, each with its date or next opening, length, spots left when few, and price.',
  placeable: true,
  options: {
    limit: { type: 'number', min: 1, max: 12, default: 4, description: 'How many offerings to show at most.' },
    heading: { type: 'string', default: 'Next up', maxLength: 80, description: 'The section heading; null for none.' },
  },
  parts: {
    section: { description: 'The whole section.', required: false },
    heading: { description: 'The section heading.', required: false, role: 'title' },
    list: { description: 'The list of offerings.', required: true },
    item: { description: "One offering; the whole item opens its page.", required: true },
    name: { description: "The service's name or the event's title.", required: true, role: 'title' },
    when: { description: "An event's day and time, or a service's next open day, in the visitor's time zone.", required: false, role: 'time' },
    length: { description: 'How long it lasts.', required: false },
    spots: { description: 'How many spots an event has left, only when few.', required: false, states: ['low'] },
    separator: { description: 'The dot between two facts on the line under the name.', required: false },
    price: { description: 'The price, from the lowest price, or Free.', required: true },
  },
  actions: {
    open: { description: "Opens an offering's page.", args: [{ name: 'offeringId', kind: 'item', list: 'offerings', key: 'id' }] },
  },
  events: {
    opened: 'A visitor opened an offering.',
  },
});

/** One offering as the piece draws it: the read's fields plus the words for them. */
export interface OfferingsItem extends SiteOfferingDto {
  /** The day it next happens, "Sat, Nov 2", in the visitor's time zone. */
  readonly day: string;
  /** The time it starts, "6:00 PM", in the visitor's time zone (a service's next open time). */
  readonly time: string;
  /** "90 min". */
  readonly length: string;
  /** "2 spots left" when an event has 3 or fewer; otherwise null. */
  readonly spots: string | null;
  /** "$65", "from $80" or "Free". */
  readonly priceLabel: string;
}

/** What the offerings piece knows. */
export interface OfferingsState {
  /** loading until the first read lands; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** The soonest offerings, soonest first, at most the limit option. */
  readonly offerings: readonly OfferingsItem[];
  /** The visitor's IANA time zone; days and times are in it. */
  readonly timezone: string;
}

/** What a drawing can ask the offerings piece to do. */
export interface OfferingsActions {
  /** Open the page of the offering with this id. */
  readonly open: (offeringId: string) => void;
}

/** What the offerings piece tells its host. */
export interface OfferingsEvents {
  /** A visitor opened an offering; carries its kind and id. */
  readonly opened: { readonly kind: 'service' | 'event'; readonly offeringId: string };
}
